/**
 * Une couleur selon l'annonce (plan media-template-generators §4.4) : des CAS — une
 * condition, une couleur — lus dans l'ordre, le premier qui passe l'emporte, et la couleur
 * du calque sinon. Le binding `fill` porte l'expression, dans le langage des textes
 * (`?{(condition) couleur}`), que le serveur évalue à l'expansion et l'éditeur à l'affichage ;
 * les cas restent à côté (`cases`) comme provenance, pour que le panneau les redessine.
 *
 * Les gardes sont rendues exclusives à l'écriture : `?{(c1) A}?{(!(c1) && c2) B}?{(!(c1) &&
 * !(c2)) D}` — aucune syntaxe nouvelle, et une expression toujours évaluable d'un seul
 * passage.
 */
import { bindingScope } from "./variable_chain";

export interface FillCase {
  when: string;
  value: string;
}

export interface FillSpec {
  expr: string;
  scope: "subject" | "company";
  cases: FillCase[];
  default: string;
}

/** L'expression d'une liste de cas ; "" sans cas (plus rien à lier). */
export function fillExpression(cases: FillCase[], fallback: string): string {
  const live = cases.filter((c) => c.when.trim() && c.value.trim());
  if (!live.length) return "";

  const negated: string[] = [];
  const blocks = live.map((c) => {
    const guard = [...negated, c.when.trim()].join(" && ");
    negated.push(`!(${c.when.trim()})`);
    return `?{(${guard}) ${c.value.trim()}}`;
  });
  blocks.push(`?{(${negated.join(" && ")}) ${fallback.trim()}}`);
  return blocks.join("");
}

/** Le binding `fill` d'une liste de cas, null sans cas — son scope suit les tokens des cas. */
export function fillSpec(cases: FillCase[], fallback: string, subjectTokens: string[] = []): FillSpec | null {
  const expr = fillExpression(cases, fallback);
  if (!expr) return null;
  return { expr, scope: bindingScope(expr, subjectTokens), cases: cases.filter((c) => c.when.trim() && c.value.trim()), default: fallback.trim() };
}

/** Une couleur plate (hex, rgb(a), nom) : ce qu'une expression de couleur doit rendre. */
export function looksLikeColor(value: string): boolean {
  return /^(#[0-9a-f]{3,8}|rgba?\(.*\)|[a-z]+)$/i.test(value.trim());
}
